import { useEffect, useRef } from 'react';

import { Permissions } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';
import { getDefaultBrandImageUrl, getFileUrl } from 'lib/core/utils';

import getTaskType from 'lib/common/contexts/ContactContext/utils/getTaskType';
import resolveCustomerProfile from 'lib/common/contexts/ContactContext/utils/resolveCustomerProfile';

import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPES from 'lib/common/constants/contactTypes';
import { ImageType } from 'lib/common/constants/imageBlockFields';

import ChatTask from 'lib/common/types/ChatTask';
import Task from 'lib/common/types/Task';
import TTask from 'lib/common/types/Task';
import connectGetter from 'lib/common/utils/connectGetter';

import { usePermissionsContext } from '../../PermissionsContext';
import { usePreferencesContext } from '../../PreferencesContext';
import { TAnyTask } from '../types/ContactContext';
import THandleContactChange from '../types/HandleContactChange';
import { getDraftTasks } from '../utils/draftTasksStorage';
import getInitialChatMessages from '../utils/getInitialChatMessages';
import handleCallRefresh from '../utils/handleCallRefresh';
import handleErrorPreviewDialingTask from '../utils/handleErrorPreviewDialingTask';
import handlePendingPreviewDialingTask from '../utils/handlePendingPreviewDialingTask';
import registerChatHandlers from '../utils/registerChatHandlers';
import registerPauseTaskEvents from '../utils/registerPauseTaskEvents';
import taskBuilder from '../utils/taskBuilder';
import useMoveTaskToACW from './useMoveTaskToACW';

interface IInitialSetup {
  getTasks: () => TAnyTask[];
  setTasks: (tasks: TAnyTask[]) => void;
  handleContactChange: THandleContactChange;
  getSelectedTaskId: () => string | undefined;
  setInitialisingTasks: (initialising: boolean) => void;
  matchNewContact: (contact: connect.Contact) => void;
}

export default function useInitialSetup({
  getTasks,
  setTasks,
  handleContactChange,
  getSelectedTaskId,
  setInitialisingTasks,
  matchNewContact,
}: IInitialSetup) {
  const { config } = useConfigContext();
  const { fetch_ } = useAuthContext();
  const { hasPermission } = usePermissionsContext();
  const {
    state: { incomingChatNotificationsEnabled },
  } = usePreferencesContext();
  const moveTaskToACW = useMoveTaskToACW({ handleContactChange });

  const incomingChatNotificationsEnabledRef = useRef(incomingChatNotificationsEnabled);

  const hasCustomerProfilesPermission = hasPermission({
    type: 'tenant',
    permission: Permissions.CUSTOMER_PROFILES,
  });

  const appLogo =
    config && config.BRAND && config.BRAND.favicon && config.BRAND.favicon.fileKey
      ? getFileUrl(config.BRAND?.favicon.fileKey, config)
      : getDefaultBrandImageUrl(ImageType.FAVICON);

  // Need a ref to avoid modifying core logic of task handling, while also ensuring the latest value is used in callbacks
  useEffect(() => {
    incomingChatNotificationsEnabledRef.current = incomingChatNotificationsEnabled;
  }, [incomingChatNotificationsEnabled]);

  useEffect(() => {
    const connect = (window as any).getConnect();

    connect.agent(async (agent: connect.Agent) => {
      const draftTasks = await getDraftTasks();

      const existingContacts: connect.Contact[] = connectGetter(agent, 'getContacts') || [];

      if (!existingContacts?.length) {
        setTasks(draftTasks.withoutContact);
        setInitialisingTasks(false);

        return;
      }

      const tasks = (
        await Promise.all(
          existingContacts.map(async (contact): Promise<Task | ChatTask | null> => {
            const taskType = getTaskType(contact);

            if (!taskType) {
              return null;
            }

            const profile = await resolveCustomerProfile({
              contact,
              config,
              fetch_,
              hasCustomerProfilesPermission,
            });

            if (taskType === CONTACT_TYPES.CHAT) {
              const chatTask = await taskBuilder.newChatTask({ contact, hasPermission });

              if (!chatTask) {
                return null;
              }

              registerChatHandlers({
                session: chatTask.chatSession,
                taskId: chatTask.taskId,
                handleContactChange,
                getSelectedTaskId,
                profile: chatTask.profile,
                icon: appLogo,
                incomingChatNotificationsEnabledRef,
                contactId: chatTask.contact.contactId,
              });

              return {
                ...chatTask,
                ...(await getInitialChatMessages(chatTask.chatSession)),
                profile,
              } as ChatTask;
            }

            const existingDraftTask = draftTasks.withContact.find((task) => task.taskId === contact.contactId);
            const taskFromContact = taskBuilder.newBaseTask(contact);
            if (!taskFromContact) {
              return null;
            }

            const task = {
              ...(existingDraftTask || {}),
              ...taskFromContact,
              profile,
              status: existingDraftTask ? CONTACT_STATES.DRAFT : taskFromContact.status,
            };

            contact.onRefresh((latestContact: connect.Contact) => {
              const existingTask = getTasks().find((existingTask: TTask) => existingTask.taskId === contact.contactId);

              // If task is in ACW, we bail out
              if (!existingTask || existingTask?.status === CONTACT_STATES.ACW) {
                return;
              }

              registerPauseTaskEvents(latestContact, getTasks, handleContactChange);

              handleCallRefresh({ handleContactChange, contact: latestContact, existingTask, moveTaskToACW });
            });

            /**
             * We're double handling ACW here, because we cannot differentiate ACW triggered by us vs. customer disconnecting.
             * If we just rely on us moving the task to acw, when the customer disconnects nothing will happen
             */
            contact.onACW((latestContact: connect.Contact) => moveTaskToACW(latestContact));

            // Since Preview Dialing tasks are VOICE tasks underneath,
            // we need to register call handlers before handling it
            if (taskType === CONTACT_TYPES.PREVIEW_DIALING) {
              const previewDialingTask = await taskBuilder.newPreviewDialingTask({ contact });

              if (!previewDialingTask) {
                return null;
              }

              // Preview dialing tasks transition into connecting state
              // once the call is placed
              contact.onConnecting(matchNewContact);

              // When preview dialing tasks are accepted,
              // they transition into pending state
              contact.onPending(handlePendingPreviewDialingTask(handleContactChange));

              contact.onError(handleErrorPreviewDialingTask(handleContactChange));

              return { ...previewDialingTask, profile } as Task;
            }

            /**
             * If call is connected, on page refresh set to a disconnected connection state
             */
            if (
              (task.type === CONTACT_TYPES.CALL || task.type === CONTACT_TYPES.QUEUE_CALLBACK) &&
              task.status === CONTACT_STATES.CONNECTED
            ) {
              try {
                return {
                  ...task,
                  audioLost: true,
                };
              } catch {
                return null;
              }
            }

            return task as Task;
          })
        )
      ).filter(Boolean) as TAnyTask[];

      setTasks([...draftTasks.withoutContact, ...tasks.filter(Boolean)]);
      setInitialisingTasks(false);
    });
  }, []);
}
